import React, { useMemo, useState } from 'react'
import { Menu, Icon } from 'semantic-ui-react'
import { translateResource, translateResourceColorRaw } from '../../../../utils/translateResource'
import { useSelector } from '../../../../hooks/useSelector'
import { BulkMinePurchaseModal } from './BulkMinePurchase'
import { resources } from '../GalacticTrader/GalacticTrader'
import { truncateCash } from '@prometheus/utils'
import usePlayerActions from '../../hooks/usePlayerActions'
import { getForecastYearOptions } from '../../../../utils/getResourceUsageForecast'
import { useForecastYears } from '../../../../hooks/useForecastYears'
import { useIsMobile } from '../../../../hooks/useIsMobile'
import { useResourceForecast } from '../../hooks/useResourceForecast'
import { ForecastYearPicker } from '../../../../components/ForecastYearPicker'
import { Button, Popup, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@prometheus/components'
import './ResourceHoverOver.scss'

export const ResourceHoverOver = () => {
  const [resource, setResource] = useState('')
  const { onUpgradePlanet } = usePlayerActions()
  const number_of_levels = useSelector(s => s.game.options?.planet_generation_options?.number_of_levels)
  const [years, setYears] = useForecastYears()
  const isMobile = useIsMobile()

  const yearOptions = useMemo(() => getForecastYearOptions(number_of_levels), [number_of_levels])
  const {
    maximum_mines,
    usage_forecast,
    production_forecast,
    net_forecast,
    current_mines,
    current_barriers,
  } = useResourceForecast(years)

  return (
    <>
      <BulkMinePurchaseModal
        open={!!resource}
        setOpen={() => setResource('')}
        resource={resource}
        mineCount={current_mines[resource] || 0}
        mineMax={maximum_mines[resource] || 0}
      />
      <Popup hoverable trigger={(
        <Menu.Item>
          <Icon name = "factory" color="yellow" />
          Mines
        </Menu.Item>
      )}>
        <div style={{ width: 'min(860px, calc(100vw - 20px))' }} className="cr-breakdown mine-breakdown">
          <div className="cr-header" style={{ justifyContent: 'center', textAlign: 'center' }}>
            Mine Counts
          </div>
          {/* Drives Use Forecast / Production / Deficit-Surplus below. Lives
              outside the table so it stays put when the table scrolls
              sideways on a phone. */}
          <ForecastYearPicker
            className="forecast-years--mine"
            years={years}
            options={yearOptions}
            onSelect={setYears}
          />
          <Table striped>
            <TableHead>
              <TableRow variant="head">
                <TableCell variant="head">Resource</TableCell>
                <TableCell variant="head">Cur/Max</TableCell>
                <TableCell variant="head">{isMobile ? 'Use' : 'Use Forecast'}</TableCell>
                <TableCell variant="head">{isMobile ? 'Prod' : 'Production'}</TableCell>
                <TableCell variant="head">{isMobile ? 'Net' : 'Deficit/Surplus'}</TableCell>
                <TableCell variant="head">Barriers</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resources.map(r => {
                const color = translateResourceColorRaw(r)
                const quickBuyDisabled = current_mines[r] === maximum_mines[r];
                return (
                  <TableRow key={r}>
                    <TableCell>
                      <Stack direction="row" space="sm">
                        <Button fullWidth onClick={() => !!maximum_mines[r] && setResource(r)} size='xs' style={{ backgroundColor: color, borderColor: color, color: 'white' }}>
                          {translateResource(r)}
                        </Button>
                        <Button disabled={quickBuyDisabled} outline size="xs" variant={quickBuyDisabled ? 'dark' : 'light'} onClick={() => onUpgradePlanet([r, Math.ceil(maximum_mines[r] / 4)], 'bulk_mine')}>
                          +25%
                        </Button>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" space="sm" style={{ alignItems: 'center' }}>
                        <Typography color={maximum_mines[r] === 0 ? 'warning' : undefined}>
                          {maximum_mines[r] === 0 ? '-' : (
                            <>
                              {truncateCash(current_mines[r], { raw: true })}/{truncateCash(maximum_mines[r], { raw: true })}
                            </>
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{truncateCash(usage_forecast[r], { raw: true })}</TableCell>
                    <TableCell>
                      <span style={{ color: production_forecast[r] > 0 ? 'var(--prometheus-primary)' : undefined }}>
                        {production_forecast[r] > 0 ? '+' : ''}{truncateCash(production_forecast[r], { raw: true })}
                      </span>
                    </TableCell>
                    <TableCell><span style={{ color: net_forecast[r] < 0 ? 'var(--prometheus-danger)' : 'var(--prometheus-primary)' }}>{truncateCash(net_forecast[r], { raw: true })}</span></TableCell>
                    <TableCell style={{ color: current_mines[r] - current_barriers[r] > 0 ? 'var(--prometheus-danger)' : 'var(--prometheus-primary)' }}>
                      {maximum_mines[r] === 0 ? (
                        <Typography color="warning">-</Typography>
                      ) : (
                        <Stack direction="row" style={{ alignItems: 'center' }}>
                          <Button size="xs" outline onClick={() => onUpgradePlanet([r, maximum_mines[r]], 'bulk_barrier')}>
                            +
                          </Button>
                          <Typography>
                            {truncateCash(current_barriers[r], { raw: true })}/{truncateCash(maximum_mines[r], { raw: true })}
                          </Typography>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      </Popup>
    </>
  )
}
